/**
 * PWA 安装能力共享单例。
 *
 * `beforeinstallprompt` 是浏览器全局一次性事件, 若多个组件各自监听并 preventDefault,
 * 只有捕获到事件的那个组件能触发系统安装弹窗。这里用模块级单例统一捕获 deferred
 * 事件, 供「右上角常驻安装按钮」与「首次进入引导弹窗」共享, 避免互相抢占。
 */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export type Platform = 'ios' | 'android' | 'harmony' | 'other';

/** 具体浏览器类型 —— 用于区分“真能一键安装”与“只能手动引导”的环境。 */
export type BrowserKind =
  | 'chrome' | 'edge' | 'samsung' | 'opera'          // 安卓/桌面：真支持 beforeinstallprompt 一键安装
  | 'quark' | 'uc' | 'huawei' | 'harmony' | 'wechat' // 国产/套壳/微信：不支持或不可靠
  | 'safari' | 'firefox' | 'other';

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
let installed = false;

function notify() {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* 单个订阅者异常不影响其他 */
    }
  });
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e: Event) => {
    // 阻止浏览器默认迷你信息栏, 由我们自己的按钮/弹窗触发
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    installed = true;
    notify();
  });
}

export function detectPlatform(): Platform {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  // 鸿蒙优先判断：HarmonyOS/OpenHarmony/华为浏览器 UA 可能同时含 Android 字样，
  // 但其原生浏览器不支持 beforeinstallprompt，需走手动「添加到桌面」引导。
  if (/HarmonyOS|OpenHarmony|HuaweiBrowser|HMSCore|ArkWeb/i.test(ua)) return 'harmony';
  if (/Android/i.test(ua)) return 'android';
  // iPadOS 13+ 伪装为 MacIntel 但带多点触控
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) return 'ios';
  return 'other';
}

/**
 * 识别具体浏览器。顺序敏感：夸克/UC/华为/鸿蒙等套壳浏览器 UA 往往也含
 * “Chrome”“Android”字样，必须先判专有标识，否则会被误判为 chrome 而显示
 * 一个点了没反应的“一键安装”按钮。
 */
export function detectBrowser(): BrowserKind {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent;
  if (/MicroMessenger/i.test(ua)) return 'wechat';
  if (/Quark/i.test(ua)) return 'quark';
  if (/UCBrowser|UCWEB|UC mini/i.test(ua)) return 'uc';
  if (/HuaweiBrowser|HMSCore/i.test(ua)) return 'huawei';
  if (/HarmonyOS|OpenHarmony|ArkWeb/i.test(ua)) return 'harmony';
  if (/Edg\//i.test(ua)) return 'edge';
  if (/SamsungBrowser/i.test(ua)) return 'samsung';
  if (/OPR\/|Opera/i.test(ua)) return 'opera';
  if (/Firefox|FxiOS/i.test(ua)) return 'firefox';
  if (/CriOS|Chrome\//i.test(ua)) return 'chrome';
  if (/Safari/i.test(ua)) return 'safari';
  return 'other';
}

/** 是否已以独立应用(standalone)方式运行 — 即已"保存到桌面"并全屏打开。 */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches === true ||
    (window.navigator as unknown as { standalone?: boolean }).standalone === true ||
    installed
  );
}

export function isMobile(): boolean {
  if (typeof navigator === 'undefined') return false;
  return detectPlatform() !== 'other' || /Mobile/i.test(navigator.userAgent);
}

/** 是否可以调用系统安装弹窗(安卓 Chrome 等捕获到 beforeinstallprompt)。 */
export function canInstall(): boolean {
  return deferredPrompt != null;
}

/** 真正支持“一键安装”的浏览器白名单（仅安卓/桌面，且非套壳）。 */
const ONE_TAP_BROWSERS: BrowserKind[] = ['chrome', 'edge', 'samsung', 'opera'];

/**
 * 当前环境是否**真正**能一键安装（点了就能弹系统安装框）。
 * 必须同时满足：① 已捕获 beforeinstallprompt；② 非 iOS；③ 非鸿蒙；④ 浏览器在白名单内。
 * 避免“部分鸿蒙/套壳浏览器下发了事件却调不起安装”导致按钮点了没反应的挫败体验——
 * 这些环境一律回退到手动引导。
 */
export function supportsOneTapInstall(): boolean {
  if (!deferredPrompt) return false;
  const p = detectPlatform();
  if (p === 'ios' || p === 'harmony') return false;
  return ONE_TAP_BROWSERS.includes(detectBrowser());
}

/** 触发系统安装弹窗; 返回用户选择结果, 无法触发时返回 null。 */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | null> {
  if (!deferredPrompt) return null;
  const evt = deferredPrompt;
  await evt.prompt();
  const choice = await evt.userChoice;
  deferredPrompt = null;
  notify();
  return choice.outcome;
}

/** 订阅安装能力状态变化(捕获到事件 / 已安装), 返回取消订阅函数。 */
export function subscribeInstall(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
